// Per-inverter data hooks (real). Latest = polled; day = on demand for charts.
import { useEffect, useState } from 'react';
import { API_BASE_URL } from '@/services/api';

export interface InvPhase { v: number | null; i: number | null; p: number | null; }
export interface InvLatest {
  unitId: number; readAtLocal: string; status: string; statusRaw: number | null;
  quality: string | null; ppvKw: number | null; pacKw: number | null;
  phaseSumKw: number | null; facHz: number | null; nominalKw: number | null;
  percentOfNominal: number | null; phases: { a: InvPhase; b: InvPhase; c: InvPhase };
  synthetic?: boolean; group?: string;
}
export interface InvDayPoint { t: string; pac: number | null; ppv: number | null; hz: number | null; va: number | null; vb: number | null; vc: number | null; ia: number | null; ib: number | null; ic: number | null; }
export interface InvDaySeries { unitId: number; points: InvDayPoint[]; }

const todayKyiv = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Kyiv' });

export type InvMode = 'per_inverter' | 'aggregate' | 'none';
export interface InvMeta { mode: InvMode; expectedInverters: number | null; availableInverters: number | null; plantPacKw: number | null; count: number; synthetic?: boolean; groupName?: string | null; }

export function useInverterLatest(assetId?: string, refreshMs = 30000) {
  const [data, setData] = useState<InvLatest[] | null>(null);
  const [meta, setMeta] = useState<InvMeta | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!assetId) { setErr(true); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/scada/inverters/${assetId}/latest`);
        if (!r.ok) throw new Error();
        const d = await r.json();
        if (!cancelled) {
          setData(d.inverters ?? []);
          setMeta({ mode: d.mode ?? 'none', expectedInverters: d.expectedInverters ?? null, availableInverters: d.availableInverters ?? null, plantPacKw: d.plantPacKw ?? null, count: d.count ?? 0, synthetic: d.synthetic ?? false, groupName: d.groupName ?? null });
          setErr(false);
        }
      } catch { if (!cancelled) setErr(true); }
    };
    load();
    const t = setInterval(load, refreshMs);
    return () => { cancelled = true; clearInterval(t); };
  }, [assetId, refreshMs]);
  return { data, meta, err };
}

export function useInverterDay(assetId?: string) {
  const [series, setSeries] = useState<InvDaySeries[] | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!assetId) { setErr(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/scada/inverters/${assetId}/day?date=${todayKyiv()}`);
        if (!r.ok) throw new Error();
        const d = await r.json();
        if (!cancelled) { setSeries(d.series ?? []); setErr(false); }
      } catch { if (!cancelled) setErr(true); }
    })();
    return () => { cancelled = true; };
  }, [assetId]);
  return { series, err };
}
